/**
 * Application Context
 *
 * Provides application context for determining which page layouts to display.
 * Each application explicitly sets its context when it mounts.
 *
 * Used by Record Pages assignment system to determine which layout to show
 * based on application context (Global, CyberSweep, Credit Repair, etc.)
 */

import React, { createContext, useContext, useState } from 'react';

export type Application =
  | 'Global'
  | 'CyberSweep'
  | 'CreditRepair'
  | 'CreditDevelopment'
  | 'CreditBlock'
  | 'IDTheftRecovery'
  | 'DebtSettlement'
  | 'RegalMain';

interface ApplicationContextType {
  currentApplication: Application;
  setCurrentApplication: (app: Application) => void;
}

const ApplicationContext = createContext<ApplicationContextType | undefined>(undefined);

export const ApplicationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentApplication, setCurrentApplication] = useState<Application>('Global');

  console.log('📱 Current Application Context:', currentApplication);

  return (
    <ApplicationContext.Provider value={{ currentApplication, setCurrentApplication }}>
      {children}
    </ApplicationContext.Provider>
  );
};

/**
 * Hook to access current application context
 *
 * @example
 * const { currentApplication, setCurrentApplication } = useApplicationContext();
 * console.log('Current app:', currentApplication); // 'CyberSweep'
 * setCurrentApplication('CreditRepair'); // Manually switch application
 */
export const useApplicationContext = () => {
  const context = useContext(ApplicationContext);
  if (!context) {
    throw new Error('useApplicationContext must be used within ApplicationProvider');
  }
  return context;
};
